import { makeObservable, observable, action, computed, runInAction } from 'mobx';
import { getMerchantList } from '@/api/merchants';
import { sessionHelper } from '@/auth/sessionHelper';
import type { Merchant } from '@/api/types';
import type { RootStore } from './RootStore';

// The central scope store. Holds the list of accessible merchants and the
// currently-selected merchant. Other stores observe selectedMerchantId via
// MobX reaction to refetch when context switches.

export class MerchantContextStore {
  @observable merchants: Merchant[] = [];
  @observable isLoading = false;
  @observable error: string | null = null;
  @observable selectedMerchantId: number | null = null;

  /**
   * Edits on screen that are not saved yet, by what they are ("instructions for FOLFOX patient"). Changing practice
   * clears the page they were made on, so a switch asks first while any are held (see `switchPractice`).
   */
  private readonly unsaved = observable.map<number, string>();
  private unsavedSeq = 0;

  // root is accepted for symmetry with sibling stores; future cross-store
  // reactions (e.g. notifying telemetry on switch) will use it.
  constructor(_root: RootStore) {
    void _root;
    makeObservable(this);
    // Restore last-selected merchant from session if any.
    this.selectedMerchantId = sessionHelper.getSelectedMerchantId();
  }

  @action
  reset(): void {
    // Clear in-memory state so the next login starts fresh, but DO NOT wipe
    // the persisted selectedMerchantId. Users want their last chosen merchant
    // restored across logout/session-expire cycles. The next loadMerchants()
    // re-validates the persisted id against the user's accessible list and
    // falls back to merchants[0] if it's no longer in scope (different
    // account, merchant retired, etc.) — so this is safe even on a shared
    // browser.
    this.merchants = [];
    this.selectedMerchantId = sessionHelper.getSelectedMerchantId();
    this.error = null;
  }

  /**
   * Hard reset — also clears the persisted merchant id. Use for explicit
   * "switch account" flows where the user should NOT be returned to their
   * last merchant on next login.
   */
  @action
  resetCompletely(): void {
    this.merchants = [];
    this.selectedMerchantId = null;
    this.error = null;
    sessionHelper.saveSelectedMerchantId(null);
  }

  get selectedMerchant(): Merchant | null {
    if (!this.merchants || this.merchants.length === 0) return null;
    return this.merchants.find((m) => m.id === this.selectedMerchantId) ?? null;
  }

  @action
  async loadMerchants(): Promise<void> {
    this.isLoading = true;
    this.error = null;
    try {
      const result = await getMerchantList({ pageSize: 200 });
      runInAction(() => {
        this.merchants = result.data ?? [];
        this.isLoading = false;

        // ── Default-selection policy ─────────────────────────────────────
        //
        // PRINCIPLE: never silently change the user's stored selection.
        //
        // Older logic auto-overwrote stored id with `merchants[0].id` any
        // time the stored id wasn't in the loaded list. That broke users:
        // a transient partial response, a brief auth hiccup, or any single
        // failed list call could replace their picked merchant with the
        // alphabetically/numerically-first one ("Default Merchant", id=0)
        // — without telling them — and their context would silently shift.
        // Once stored as 0, the new default looked legitimate on every
        // future page load.
        //
        // New policy:
        //   - First-time user (no stored id): auto-pick merchants[0] as a
        //     reasonable default so the UI isn't blank.
        //   - Returning user (stored id present): KEEP IT, period. If the
        //     id isn't in the current list, the chip will fall back to
        //     "Pick a merchant" so the user knows something's off — better
        //     than a silent switch. The next successful fetch will resolve
        //     the name. If the merchant truly is gone (access revoked,
        //     retired), the user will re-pick.
        const stored = this.selectedMerchantId;
        if (stored == null) {
          if (this.merchants[0]) this.selectMerchant(this.merchants[0].id);
        } else if (!this.merchants.some((m) => m.id === stored)) {
          // Log to console (and downstream telemetry) so a pathological
          // case — a user persistently seeing "Pick a merchant" because
          // their stored id is genuinely gone — is debuggable.
          // eslint-disable-next-line no-console
          console.warn(
            `[MerchantContext] Stored merchant id ${stored} not in loaded list ` +
              `(${this.merchants.length} merchant(s)). Keeping the stored id; ` +
              `user can re-pick from the chip if needed.`,
          );
        }
      });
    } catch (err) {
      runInAction(() => {
        this.isLoading = false;
        this.error = err instanceof Error ? err.message : 'Failed to load merchants';
      });
    }
  }

  @action
  selectMerchant(merchantId: number | null): void {
    this.selectedMerchantId = merchantId;
    sessionHelper.saveSelectedMerchantId(merchantId);
  }

  /** Marks unsaved work on screen until the returned release is called (the edit was saved, discarded or closed). */
  @action
  holdUnsaved(label: string): () => void {
    const id = ++this.unsavedSeq;
    this.unsaved.set(id, label);
    return action(() => {
      this.unsaved.delete(id);
    });
  }

  /** What a practice switch would discard right now. */
  @computed get unsavedWork(): string[] {
    return [...this.unsaved.values()];
  }
}
